import React from "react";
import { Box, Container, Grid, Typography, IconButton, Divider } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Icons

import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TrainIcon from "@mui/icons-material/Train";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import HotelIcon from "@mui/icons-material/Hotel";
import PersonIcon from "@mui/icons-material/Person";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";

const Footer = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const services = [
    { title: "Hire Vehicle", icon: <DirectionsCarIcon fontSize="small" />, path: "/booking" },
    { title: "Book Train Ticket", icon: <TrainIcon fontSize="small" />, path: "/booking" },
    { title: "Book Bus Ticket", icon: <DirectionsBusIcon fontSize="small" />, path: "/booking" },
    { title: "Book Flight Ticket", icon: <FlightTakeoffIcon fontSize="small" />, path: "/booking" },
    { title: "Hire Guide", icon: <PersonIcon fontSize="small" />, path: "/booking" },
    { title: "Currency Exchange", icon: <CurrencyExchangeIcon fontSize="small" />, path: "/booking" },
    { title: "Hotel Booking", icon: <HotelIcon fontSize="small" />, path: "/booking" }
  ];

  return (
    <Box 
      component={motion.div}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-200px" }}
      transition={{ duration: 0.6 }}
      sx={{
        width: "100%",
        background: "linear-gradient(135deg, #db352a 0%, #871e19 100%)",
        pt: 8,
        pb: 4,
        position: "relative",
        overflow: "hidden",
        boxShadow: "0px -10px 30px rgba(0,0,0,0.1)"
      }}
    >
      {/* Wave Pattern Element */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "url('/images/world-map-dots.png')",
          backgroundSize: "cover",
          opacity: 0.05,
          zIndex: 0
        }}
      />

      {/* Logo Section */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center",
            mb: 6
          }}>
            <Box 
              sx={{ 
                display: "flex",
                alignItems: "center",
                mb: 2
              }}
            >
              <FlightTakeoffIcon 
                sx={{ 
                  color: "white", 
                  fontSize: "2.5rem",
                  mr: 1,
                  transform: "rotate(-15deg)"
                }} 
              />
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  letterSpacing: "1px"
                }}
              >
                Ashoka Tours
              </Typography>
            </Box>
            
            <Typography 
              variant="body1" 
              color="rgba(255,255,255,0.8)"
              sx={{ textAlign: "center", maxWidth: "600px" }}
            >
              Your trusted partner for all travel needs in Aurangabad and beyond.
              Explore the beauty of India with our premium services.
            </Typography>
            

          </Box>
        </motion.div>

        <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 6 }} />

        {/* Main Footer Links */}
        <Grid 
          container 
          spacing={4}
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Company Column */}
          <Grid item xs={12} sm={6} md={4} component={motion.div} variants={itemVariants}>
            <Typography
              variant="h6"
              color="white"
              gutterBottom
              sx={{ 
                textTransform: "uppercase", 
                mb: 3,
                fontWeight: "bold",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "40px",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  bottom: "-8px",
                  left: 0
                }
              }}
            >
              Explore
            </Typography>
            
            {[
              { title: "Services", path: "/services" },
              { title: "Tour Packages", path: "/tour-packs" },
              { title: "About Us", path: "/about-us" },
              { title: "Contact Us", path: "/contact-us" }
            ].map((item, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400 }}>
                  <Link
                    to={item.path}
                    style={{ 
                      color: "white", 
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <Box 
                      component="span" 
                      sx={{ 
                        width: "6px", 
                        height: "6px", 
                        borderRadius: "50%", 
                        bgcolor: "white",
                        display: "inline-block",
                        mr: 1.5
                      }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: "light" }}>
                      {item.title}
                    </Typography>
                  </Link>
                </motion.div>
              </Box>
            ))}
          </Grid>

          {/* Services Column */}
          <Grid item xs={12} sm={6} md={4} component={motion.div} variants={itemVariants}>
            <Typography
              variant="h6"
              color="white"
              gutterBottom
              sx={{ 
                textTransform: "uppercase", 
                mb: 3,
                fontWeight: "bold",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "40px",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  bottom: "-8px",
                  left: 0
                }
              }}
            >
              Services
            </Typography>
            
            {services.map((service, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <motion.div whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400 }}>
                  <Link
                    to={service.path}
                    style={{ 
                      color: "white", 
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>
                      {service.icon}
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: "light" }}>
                      {service.title}
                    </Typography>
                  </Link>
                </motion.div>
              </Box>
            ))}
          </Grid>

          {/* Get in Touch Column */}
          <Grid item xs={12} sm={6} md={4} component={motion.div} variants={itemVariants}>
            <Typography
              variant="h6"
              color="white"
              gutterBottom
              sx={{ 
                textTransform: "uppercase", 
                mb: 3,
                fontWeight: "bold",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: "40px",
                  height: "3px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  bottom: "-8px",
                  left: 0
                }
              }}
            >
              Get in Touch
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mb: 1 }}>
                Feel free to contact us anytime. We're here to help!
              </Typography>
              
              {[
                { icon: <PhoneIcon fontSize="small" />, text: "+91 9890340816" },
                { icon: <PhoneIcon fontSize="small" />, text: "+91 9890565100" },
                { icon: <PhoneIcon fontSize="small" />, text: "+91 9922063222" },
                { icon: <EmailIcon fontSize="small" />, text: "atkadam88@gmail.com", link: "mailto:atkadam88@gmail.com" }
              ].map((item, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center",
                    mb: 1.5
                  }}
                >
                  <Box sx={{ mr: 1.5, color: "rgba(255,255,255,0.8)" }}>
                    {item.icon}
                  </Box>
                  
                  {item.link ? (
                    <Link
                      to={item.link}
                      style={{
                        color: "white",
                        textDecoration: "none"
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "light" }}>
                        {item.text}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="body1" color="white" sx={{ fontWeight: "light" }}>
                      {item.text}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            
            {/* Location Information */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ fontWeight: "medium" }}>
                Hotel Raviraj, Dr Rajendra Prasad Marg, Adalat Rd, near Baba Petrol Pump, Chhatrapati Sambhaji Nagar, Maharashtra 431005
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Copyright Section */}
      <Box
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          mt: 8,
          pt: 3,
          px: { xs: 2, sm: 4 },
          position: "relative",
          zIndex: 1
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "flex-start" }
          }}>
            <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mb: { xs: 1, sm: 0 } }}>
              &copy; {new Date().getFullYear()} Ashoka Tours and Travel. All rights reserved.
            </Typography>
            
            <Typography variant="body2" color="rgba(255,255,255,0.8)">
              <motion.div whileHover={{ color: "#fff" }}>
                <a
                  href="https://kpswebdev.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  Design And Developed By KPS
                </a>
              </motion.div>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;